import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  getLatestAssessment,
  getAssessment,
  toggleTask,
  reportUrl,
  getIntegrationStatus,
  simulateScore,
} from "@/lib/api";
import NavBar from "@/components/NavBar";
import TrustGauge from "@/components/TrustGauge";
import AnimatedNumber from "@/components/AnimatedNumber";
import CategoryCard from "@/components/CategoryCard";
import TrustLeaks from "@/components/TrustLeaks";
import PriorityRoadmap from "@/components/PriorityRoadmap";
import CategoryRadar from "@/components/CategoryRadar";
import TrendChart from "@/components/TrendChart";
import CategoryDetailDialog from "@/components/CategoryDetailDialog";
import CompetitorsSoon from "@/components/CompetitorsSoon";
import CompetitorPanel from "@/components/CompetitorPanel";
import WidgetPanel from "@/components/WidgetPanel";
import IntegrationBanner from "@/components/IntegrationBanner";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { Download, Plus, ArrowRight, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogCategory, setDialogCategory] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  // --- Simulation state (never persisted) ---
  const [simulationMode, setSimulationMode] = useState(false);
  const [simulatedResolved, setSimulatedResolved] = useState([]);
  const [projection, setProjection] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [data, status] = await Promise.all([
          id ? getAssessment(id) : getLatestAssessment(),
          getIntegrationStatus().catch(() => null),
        ]);
        setAssessment(data);
        setIntegrationStatus(status);
      } catch (e) {
        if (e?.response?.status === 404) {
          navigate("/new", { replace: true });
          return;
        }
        toast.error("Could not load assessment");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleTaskToggle = async (findingId) => {
    if (!assessment) return;
    const prev = assessment.completed_tasks || [];
    const nextIsAdd = !prev.includes(findingId);
    // optimistic
    setAssessment({
      ...assessment,
      completed_tasks: nextIsAdd
        ? [...prev, findingId]
        : prev.filter((x) => x !== findingId),
    });
    try {
      const res = await toggleTask(assessment.assessment_id, findingId);
      setAssessment((a) => ({ ...a, completed_tasks: res.completed_tasks }));
      if (nextIsAdd) toast.success("Task marked complete", { description: "+ trust momentum" });
    } catch {
      toast.error("Could not update task");
      setAssessment((a) => ({ ...a, completed_tasks: prev }));
    }
  };

  const handleDownload = () => {
    if (!assessment) return;
    window.open(reportUrl(assessment.assessment_id), "_blank");
  };

  // --- Simulation ---
  // Whenever simulatedResolved changes AND simulation mode is on, hit the
  // /simulate endpoint to get a deterministic projection from the backend.
  useEffect(() => {
    if (!assessment || !simulationMode) return;
    let cancelled = false;
    (async () => {
      setSimulating(true);
      try {
        const res = await simulateScore(
          assessment.assessment_id,
          simulatedResolved,
        );
        if (!cancelled) setProjection(res);
      } catch {
        if (!cancelled) toast.error("Could not simulate score");
      } finally {
        if (!cancelled) setSimulating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assessment, simulationMode, simulatedResolved]);

  const handleSimulationToggle = (next) => {
    setSimulationMode(next);
    if (!next) {
      setSimulatedResolved([]);
      setProjection(null);
    }
  };

  const handleSimulatedToggle = (findingId) => {
    setSimulatedResolved((prev) =>
      prev.includes(findingId)
        ? prev.filter((x) => x !== findingId)
        : [...prev, findingId],
    );
  };

  const handleSimulationReset = () => {
    setSimulatedResolved([]);
    setProjection(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex items-center justify-center">
        <div className="pp-label" data-testid="dashboard-loading">
          Loading your assessment…
        </div>
      </div>
    );
  }
  if (!assessment) return null;

  const {
    business_name,
    website,
    industry,
    overall_score,
    overall_confidence,
    evidence_coverage,
    categories,
    biggest_leaks,
    roadmap,
    history,
    narration,
    completed_tasks,
  } = assessment;

  // --- Compute display values that respect simulation mode ---
  const isSimActive =
    simulationMode && projection && simulatedResolved.length > 0;
  const displayScore = isSimActive
    ? projection.projected.overall_score
    : overall_score;
  const projectedByCategoryId = new Map(
    isSimActive
      ? projection.categories.map((c) => [c.id, c.projected_score])
      : [],
  );
  const displayCategories = isSimActive
    ? categories.map((c) => ({
        ...c,
        score: projectedByCategoryId.get(c.id) ?? c.score,
      }))
    : categories;
  const scoreDelta = displayScore - overall_score;
  const coveragePct = evidence_coverage?.coverage_pct ?? 0;
  const categoriesMeasured = evidence_coverage?.categories_measured ?? 0;
  const categoriesTotal = evidence_coverage?.categories_total ?? categories?.length ?? 8;
  const fullCoverage = categoriesMeasured === categoriesTotal;
  // Phase 7 (v1 launch cleanup): reuse the backend's own overall_confidence
  // tri-state (verified/calculated/unavailable) rather than inventing a new
  // coverage threshold in the UI — "unavailable" already means coverage
  // fell below the same 50% measurable-scope bar used throughout
  // scoring_confidence.py.
  const insufficientEvidence = (overall_confidence || "calculated") === "unavailable";
  // Deterministic, score-derived fallback — never a static negative claim.
  // narration.executive_summary is populated either by the LLM narrator or
  // by ai_recommender.py's own deterministic fallback; this only covers the
  // brief window before either has run (narration is still null).
  const executiveSummary =
    narration?.executive_summary ||
    (typeof overall_score === "number"
      ? `Your Trust Score is ${overall_score}/100. A detailed summary is being prepared.`
      : "Your trust assessment is being prepared.");

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <NavBar />
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-10">
        {/* Executive header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="pp-label mb-2 flex items-center gap-2">
              Trust Assessment
              <ConfidenceBadge confidence={overall_confidence || "calculated"} />
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-white leading-tight" data-testid="dashboard-business-name">
              {business_name}
            </h1>
            <div className="text-sm text-[#C7CBD1] mt-1">
              {website} · {industry}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate("/new")}
              className="pp-btn-secondary text-sm flex items-center gap-2"
              data-testid="dashboard-new-assessment"
            >
              <Plus className="h-4 w-4" /> New assessment
            </button>
            <button
              onClick={handleDownload}
              className="pp-btn-primary text-sm flex items-center gap-2"
              data-testid="dashboard-download-report"
            >
              <Download className="h-4 w-4" /> Download report
            </button>
          </div>
        </div>

        <div className="mb-6">
          <IntegrationBanner status={integrationStatus} />
        </div>

        {/* Hero: Gauge + Opportunity + belief */}
        <div className="grid lg:grid-cols-[380px_1fr] gap-6 mb-6">
          <div className="pp-card p-8 flex flex-col items-center justify-center" data-testid="dashboard-hero-gauge">
            <TrustGauge
              score={displayScore}
              label={isSimActive ? "PROJECTED SCORE (SIMULATION)" : "TRUST SCORE"}
              modelEstimate={(overall_confidence || "calculated") !== "verified"}
              insufficientEvidence={insufficientEvidence}
            />
            {isSimActive && (
              <div
                className="mt-4 text-xs text-[#C7CBD1] flex items-center gap-2"
                data-testid="simulation-delta"
              >
                <span className="pp-label text-[10px]">from {overall_score}</span>
                <span className="pp-badge-green">
                  {scoreDelta >= 0 ? "+" : ""}
                  {scoreDelta} projected
                </span>
              </div>
            )}
            <div className="mt-6 flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="pp-btn-secondary text-xs"
                data-testid="hero-download"
              >
                Download report
              </button>
              <a
                href="#leaks"
                className="pp-btn-primary text-xs flex items-center gap-2"
                data-testid="hero-view-leaks"
              >
                View trust leaks <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          <div className="pp-card p-8 flex flex-col justify-between" data-testid="dashboard-hero-coverage">
            <div>
              <div className="pp-label mb-3 flex items-center gap-2">
                <span>Assessment coverage</span>
                {!fullCoverage && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border"
                    style={{
                      color: "#EAB308",
                      borderColor: "#EAB30855",
                      background: "#EAB3081A",
                    }}
                    title="Some areas could not be measured for this business and were left out of the score entirely — never scored as a weakness."
                    data-testid="coverage-partial-badge"
                  >
                    Partial
                  </span>
                )}
              </div>
              <div
                className="font-display text-6xl lg:text-7xl text-white leading-none metric font-bold"
                data-testid="dashboard-coverage-pct"
              >
                <AnimatedNumber value={coveragePct} suffix="%" />
              </div>
              <div className="text-sm text-[#C7CBD1] mt-3">
                {categoriesMeasured} of {categoriesTotal} trust areas measured
              </div>
              <div className="text-xs text-[#C7CBD1] mt-3 leading-relaxed max-w-md">
                Areas we couldn't measure are excluded from the score entirely —
                never counted against this business. A high score built on
                partial coverage is not the same as a fully substantiated one;
                read this alongside the {overall_confidence || "calculated"}{" "}
                badge above.
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="pp-surface p-4" data-testid="dashboard-trust-ceiling">
                  <div className="pp-label text-[10px] mb-1">Trust Ceiling</div>
                  <div className="metric text-3xl font-bold text-white leading-none">
                    <AnimatedNumber value={assessment.trust_ceiling ?? overall_score} />
                    <span className="text-sm text-[#C7CBD1]"> / 100</span>
                  </div>
                  <div className="text-[11px] text-[#C7CBD1] mt-2 leading-snug">
                    Reachable once the full roadmap is complete.
                  </div>
                </div>
                <div className="pp-surface p-4">
                  <div className="pp-label text-[10px] mb-1">Room to grow</div>
                  <div className="metric text-3xl font-bold text-[#6CC24A] leading-none">
                    +
                    <AnimatedNumber
                      value={(assessment.trust_ceiling ?? overall_score) - overall_score}
                    />
                  </div>
                  <div className="text-[11px] text-[#C7CBD1] mt-2 leading-snug">
                    Points you can add by fixing every current leak.
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <div className="pp-label mb-3">Executive summary</div>
              <p className="text-white leading-relaxed max-w-2xl">
                {executiveSummary}
              </p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="pp-badge-neutral">
                <TrendingUp className="h-3 w-3 text-[#6CC24A]" />
                {biggest_leaks?.length || 0} high-impact leaks
              </span>
              <span className="pp-badge-neutral">
                {(roadmap?.today?.length || 0) +
                  (roadmap?.this_week?.length || 0) +
                  (roadmap?.this_month?.length || 0)}{" "}
                roadmap steps
              </span>
            </div>
          </div>
        </div>

        {/* Category grid */}
        <div className="mb-6" id="categories">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="pp-label">Trust categories</div>
              <div className="font-display text-xl text-white">
                Where trust is earned — and where it's leaking.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {displayCategories.map((c, i) => (
              <CategoryCard
                key={c.id}
                category={c}
                index={i}
                onOpen={() => setDialogCategory(c)}
              />
            ))}
          </div>
        </div>

        {/* Leaks + Roadmap */}
        <div id="leaks" className="grid grid-cols-1 gap-6 mb-6">
          <TrustLeaks
            leaks={biggest_leaks || []}
            narration={narration}
            onFix={(leak) => {
              const cat = categories.find((c) => c.id === leak.category_id);
              if (cat) setDialogCategory(cat);
            }}
          />
          <PriorityRoadmap
            roadmap={roadmap}
            completedTasks={completed_tasks || []}
            onToggleTask={handleTaskToggle}
            narration={narration}
            simulationMode={simulationMode}
            simulatedResolved={simulatedResolved}
            nonSimulatableIds={projection?.non_simulatable_finding_ids || []}
            onSimulationToggle={handleSimulationToggle}
            onSimulatedToggle={handleSimulatedToggle}
            onSimulationReset={handleSimulationReset}
            simulating={simulating}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <CategoryRadar categories={categories} />
          <TrendChart history={history || []} />
        </div>

        {/* Competitors + Widget */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          {integrationStatus?.competitor_index?.configured ? (
            <CompetitorPanel assessment={assessment} />
          ) : (
            <CompetitorsSoon status={integrationStatus?.competitor_index} />
          )}
          <WidgetPanel assessmentId={assessment.assessment_id} />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="pp-surface p-6 flex flex-wrap items-center justify-between gap-4"
        >
          <div>
            <div className="pp-label mb-1">Ready for the next step?</div>
            <div className="font-display text-lg text-white">
              Book a Trust Review with Pulse Point — same standards, no pressure.
            </div>
          </div>
          <a
            href="https://officialpulsepoint.com/free-trust-audit/"
            target="_blank"
            rel="noreferrer"
            className="pp-btn-primary flex items-center gap-2"
            data-testid="dashboard-cta-trust-review"
          >
            Schedule review <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>
      </div>

      <CategoryDetailDialog
        open={!!dialogCategory}
        onOpenChange={(o) => !o && setDialogCategory(null)}
        category={dialogCategory}
        narration={narration}
      />
    </div>
  );
}
